(ns practitest.frontend.routing.router
  (:require [clojure.string :as str]
            [practitest.frontend.common.dom :as dom]
            [re-frame.core :as re-frame]
            [reitit.core :as r]
            [reitit.frontend :as rf]
            [reitit.frontend.controllers :as rfc]
            [reitit.frontend.history :as rfh]
            [cljs.pprint]
            [reitit.impl :as impl]
            [practitest.frontend.common.utils :as utils]))

(re-frame/reg-sub
  ::current-route
  (fn [db _]
    (:current-route db)))

(defonce router (atom nil))

(re-frame/reg-fx
  :push-state
  (fn [route]
    (apply rfh/push-state @router route)))

(re-frame/reg-fx
  :replace-state
  (fn [route]
    (apply rfh/replace-state @router route)))

(defn- enrich-path-params
  [db route-vector]
  (let [current-route (get db :current-route)
        [name path-params query-params] route-vector
        extractors (:extractors (:data current-route))
        ;; enrich path-params here
        path-params (reduce
                      (fn [params extractor]
                        (merge params (extractor db)))
                      path-params
                      extractors)]
    [name path-params query-params]))

(re-frame/reg-event-fx
  ::push-state
  (fn [{db :db} [_ & route]]
    (when (vector? (first route))
      (js/console.warn "invalid nesting, correct usage: [::router/push-state :route-name path-params query-params]"))
    {:push-state (enrich-path-params db route)}))

(re-frame/reg-event-fx
  ::replace-state
  (fn [{db :db} [_ & route]]
    (when (vector? (first route))
      (js/console.warn "invalid nesting, correct usage: [::router/replace-state :route-name path-params query-params]"))
    {:replace-state (enrich-path-params db route)}))

;; TODO: own version of controllers, getting snapshot of DB ?

(re-frame/reg-event-db
  ::set-current-route
  (fn [db [_ new-match]]
    (let [old-match (:current-route db)
          controllers (rfc/apply-controllers (:controllers old-match) new-match)]
      ;; TODO: handle project changes here ?
      (assoc db :current-route (assoc new-match :controllers controllers)))))

(re-frame/reg-event-db
  ::add-form-id
  (fn [db [_ form-id]]
    (update db :form-ids (fnil conj #{}) form-id)))

(re-frame/reg-event-db
  ::remove-form-id
  (fn [db [_ form-id]]
    (update db :form-ids disj form-id)))

(defn get-view
  [route]
  (if-let [view-fn (:view (:data route))]
    (view-fn route)

    ;; Default view
    [:div
     [:h1 "No page yet"]
     [:h3 "Match data"]
     [:pre (with-out-str
             (cljs.pprint/pprint route))]]))

(defn get-current-route
  []
  (:current-route @router))

;; Fragment manipulation support

(defn- encode-fragment [params]
  (impl/query-string params))

(defn- decode-fragment [s]
  (when s
    (->> (.entries (js/URLSearchParams. s))
         (map (fn [[k v]] [(keyword k) v]))
         (into {}))))

(defn get-current-fragment-decoded []
  (when-let [fragment (:fragment (get-current-route))]
    (decode-fragment fragment)))

(defn- get-current-route-from-location []
  ;; We can't really use (get-current-route) because filters tree messes up with URL
  ;; And current-route doesn't have query-params associated so it will get erased
  ;; So we re-match with current URL here to have all query params preserved
  ;; TODO: fix this in filters tree
  (rf/match-by-path (:router @router) (dom/get-current-href-relative)))

(defn get-current-query-params []
  (:query-params (get-current-route-from-location)))

(defn match->path
  [match]
  (rf/match->path match (:query-params match) (:fragment match)))

(defn- replace-current-route-with-params!
  [query-params fragment-map]
  (when-let [route (get-current-route-from-location)]
    (let [newroute (if (empty? fragment-map)
                     (dissoc route :fragment)
                     (assoc route :fragment (encode-fragment fragment-map)))
          newroute (if (empty? query-params)
                     (dissoc newroute :query-params)
                     (assoc newroute :query-params query-params))
          path (match->path newroute)]
      (swap! router assoc :current-route newroute)
      ;; TODO: on-navigate call ? - can trigger controllers (need to check though), which is undesirable
      ;; TODO: current-route in DB stays old - do we need to fix it ?
      ;; TODO: pushState here ?
      (.replaceState js/history nil "" path))))

(defn update-fragment!
  "Updates current route fragment map with function f applied to
   existing fragment map and additional args passed along.
   Returns new fragment map for current route.
   Similar to `update` function"
  [f & xs]
  (let [fragment (get-current-fragment-decoded)
        query-params (get-current-query-params)]
    (replace-current-route-with-params! query-params (apply f fragment xs))
    (get-current-fragment-decoded)))

(defn update-query-params!
  "Updates current route query params with function f applied to
   existing query params and additional args passed along.
   Returns new query-params for current route.
   Similar to `update` function"
  [f & xs]
  (let [query-params (get-current-query-params)
        fragment (get-current-fragment-decoded)]
    (replace-current-route-with-params! (apply f query-params xs) fragment)
    (get-current-query-params)))

;; We don't need reactivity here, so we're just using simple atom
;; It will store a map of form-id -> dirty-fields subscription
(defonce ^:private forms-dirty-fields (atom {}))

(defn add-dirty-fields! [form-id dirty-fields]
  (swap! forms-dirty-fields assoc form-id dirty-fields))

(defn remove-dirty-fields! [form-id]
  (swap! forms-dirty-fields dissoc form-id))

(defn any-form-dirty? []
  (some #(boolean (seq (deref %)))
        (vals @forms-dirty-fields)))

;;; tracking back button
;; Now this is quite ugly and wouldn't work reliably
;; but we don't care much, we want to catch 80% of cases here
(defonce previous-route (atom nil))

(defn- on-navigate
  [match this]
  (let [first-launch? (nil? @router)
        ;; Previous route would be the current one before change
        prev-route (:current-route @router)]
    (when first-launch?
      ;; init on first on-navigate
      (reset! router this))

    (swap! router assoc :current-route match)

    ;; Don't depend on this logic if you want to reliably catch "Back" button !
    ;; We care only if it's maybe a back button navigation and we have dirty forms
    ;; and we need to cancel standard flow
    (if (and (= match @previous-route)
             (any-form-dirty?))
      (do
        ;; Ok, so we're thinking that we're in dirty forms scenario, so we need to do some trickery
        ;; popstate is triggered after URL is already changed so we need to replace it back to
        ;; whatever it was before back navigation event received (which whould be `:current-route` in this case)
        ;; We're using pushState because we want Back button to work correctly after we e.g. saved our form
        (.pushState js/window.history nil "" (match->path prev-route))
        ;; Trigger reload to trigger popstate handler that will show warning
        ;; again - ugly but will be better than losing user input completely
        (.reload js/window.location))
      ;; Normal flow - change view, update title, etc
      (do
        ;; Store previous current route
        (reset! previous-route prev-route)

        (when match
          (when-let [title (:title (:data match))]
            (dom/update-page-title title))

         ;; We have unfortunate circular dependencies on db state and fetch events
         ;; so we're using dispatch-sync on initial load to populate some portions of db
          (if first-launch?
            (re-frame/dispatch-sync [::set-current-route match])
            (re-frame/dispatch [::set-current-route match])))))))

(defn init!
  [routes]
  ;; Restart router if already started
  (swap! router rfh/stop!)

  (rfh/start! (r/router routes)
              on-navigate
              {:use-fragment false
               :ignore-anchor-click? (fn [router e el uri]
                                       (let [match (rfh/ignore-anchor-click? router e el uri)]
                                         (cond
                                           ;; Special case for forced-reload navigation
                                           (:force-reload? (:data match))
                                           nil

                                           ;; Special hack to trigger beforeunload event when links are clicked and current
                                           ;; form is modified to show user a warning
                                           (any-form-dirty?)
                                           nil

                                           :else
                                           match)))}))

(defn get-href
  ([name path-params] (get-href name path-params nil nil))
  ([name path-params query-params fragment-map]
   (let [match (rf/match-by-name (:router @router) name path-params)]
     (match->path (cond-> match
                          (seq query-params) (assoc :query-params query-params)
                          (seq fragment-map) (assoc :fragment (encode-fragment fragment-map)))))))

(defn get-href2
  "Wrapper for get-href, applying kebab-case for map keys"
  ([name path-params] (get-href2 name path-params nil nil))
  ([name path-params query-params fragment-map]
   (get-href name (utils/->kebab-case-keys path-params) query-params fragment-map)))
